import { PageRoute } from "..";
import { ThemeProvider } from "@emotion/react";
import { noop } from "lodash";
import { PureComponent } from "react";
import { connect } from "react-redux";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { Dispatch } from "redux";

import Redirect from "components/Redirect";
import { Grid, GridColumnType, ViewStack } from "components/grid";
import { isInIframe } from "helpers/app";
import { removeParamFromUrl } from "helpers/url";
import { IStoreState } from "interfaces/storeState";
import { AutomationsRoutes } from "routes/automations/constants";
import CustomViews from "routes/settings/customViews/CustomViews";
import { withRememberLastRoute } from "routes/withRememberLastRoute";
import { SettingsViewActions, getFocusedColumn } from "store/views/settings";
import { legacyTheme } from "themes/legacyTheme";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import CannedResponses from "./canned-responses";
import { SettingsRoutes } from "./constants";
import ContactForm from "./contact-form";
import { CustomFields } from "./custom-fields";
import EmailTemplate from "./email-template";
import { Email } from "./inboxes/Email";
import Integrations from "./integrations";
import { LiveChatChannel } from "./livechat/LiveChatChannel";
import SettingsSideNavigation from "./navigation/SettingsSideNavigation";
import Notifications from "./notifications";
import { OpenWidget } from "./open-widget";
import Spam from "./spam";
import Tags from "./tags";

interface IStateProps {
  focusedColumn: GridColumnType;
}

interface IDispatchProps {
  setTeamId(teamId: string): void;
  onFocusFirstColumn(): void;
}

interface IMatchProps {
  teamId?: string;
}

interface IProps extends IStateProps, IDispatchProps, RouteComponentProps<IMatchProps> {}

class Router extends PureComponent<IProps> {
  componentDidMount(): void {
    removeParamFromUrl("team_id", this.props.location, this.props.history)
      // @ts-expect-error TS(2345): Argument of type '(teamId: string) => void' is not... Remove this comment to see the full error message
      .then((teamId: string) => {
        this.props.setTeamId(teamId);
      })
      .catch(noop);
  }

  render(): React.ReactNode {
    const { focusedColumn, onFocusFirstColumn, location } = this.props;
    const redirectToFirstRoute = <Redirect to="/settings/email-addresses" />;
    const isEmbeddedInIframe = isInIframe();

    const sidebarHidden =
      [
        `${SettingsRoutes.EmailTemplates}/add`,
        `${SettingsRoutes.EmailTemplates}/edit`,
        `${SettingsRoutes.EmailTemplates}/duplicate`,
        `${SettingsRoutes.ContactForms}/add`,
        `${SettingsRoutes.ContactForms}/duplicate`,
        `${SettingsRoutes.ContactForms}/edit`,
      ].some(path => location.pathname.startsWith(path)) || isEmbeddedInIframe;

    return (
      <Grid
        relativeWidths={sidebarHidden ? [1] : [1, 10]}
        minWidths={sidebarHidden ? [0] : [270, 0]}
        zIndices={sidebarHidden ? [0] : [0, 1]}
        focusedColumn={focusedColumn}
        overflowHidden
        noBorder={isEmbeddedInIframe}
      >
        {!sidebarHidden && (
          <ViewStack>
            <ThemeProvider theme={legacyTheme}>
              <SettingsSideNavigation />
            </ThemeProvider>
          </ViewStack>
        )}
        <ViewStack>
          <ThemeProvider theme={legacyTheme}>
            <Switch>
              <Route
                exact
                path="/settings"
                render={() => {
                  onFocusFirstColumn();
                  return redirectToFirstRoute;
                }}
              />
              <PageRoute path={SettingsRoutes.EmailAddress} documentTitle="Email" component={Email} />
              <Route path="/settings/inboxes">
                <Redirect to={SettingsRoutes.EmailAddress} />
              </Route>
              <PageRoute
                path={SettingsRoutes.CannedResponses}
                documentTitle="Canned responses"
                component={CannedResponses}
              />
              <PageRoute path={SettingsRoutes.Tags} documentTitle="Tags" component={Tags} />
              <PageRoute path={SettingsRoutes.CustomFields} documentTitle="Custom fields" component={CustomFields} />
              {/* @ts-expect-error TS(2769) */}
              <PageRoute path={SettingsRoutes.Notifications} documentTitle="Notifications" component={Notifications} />
              <PageRoute path={SettingsRoutes.MyViews} documentTitle="Ticket views" component={CustomViews} />
              <PageRoute path={SettingsRoutes.Integrations} documentTitle="Integrations" component={Integrations} />
              <Route path={SettingsRoutes.Workflows}>
                <Redirect from={SettingsRoutes.Workflows} to={AutomationsRoutes.Rules} />
                <Redirect from={`${SettingsRoutes.Workflows}/add`} to={AutomationsRoutes.AddRule} />
                <Redirect from={`${SettingsRoutes.Workflows}/edit/:id`} to={`${AutomationsRoutes.EditRule}/:id`} />
              </Route>
              <PageRoute
                path={SettingsRoutes.EmailTemplates}
                documentTitle="Email templates"
                component={EmailTemplate}
              />
              <PageRoute path={SettingsRoutes.ContactForms} documentTitle="Contact forms" component={ContactForm} />
              <PageRoute path={SettingsRoutes.WidgetForms} documentTitle="Widget forms" component={ContactForm} />
              <Redirect from={SettingsRoutes.ContactForms} to={SettingsRoutes.ContactForms} />
              <PageRoute path={SettingsRoutes.Spam} documentTitle="Spam" component={Spam} />
              <PageRoute path={SettingsRoutes.OpenWidget} documentTitle="Contact widget" component={OpenWidget} />
              <PageRoute path={SettingsRoutes.LiveChat} documentTitle="LiveChat" component={LiveChatChannel} />
              {redirectToFirstRoute}
            </Switch>
          </ThemeProvider>
        </ViewStack>
      </Grid>
    );
  }
}

const mapStateToProps = (state: IStoreState): IStateProps => ({
  focusedColumn: getFocusedColumn(state),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  // @ts-expect-error TS(2345)
  setTeamId: (teamId: string) => dispatch(SettingsViewActions.setTeamId({ teamId })),
  // @ts-expect-error TS(2345)
  onFocusFirstColumn: () => dispatch(SettingsViewActions.setFocusedColumn({ focusedColumn: GridColumnType.First })),
});

export default withRememberLastRoute(connect(mapStateToProps, mapDispatchToProps)(Router), "/settings");
